import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Sparkles, BookOpen, Music } from 'lucide-react';
import { Header } from './Header';
interface ChatModeScreenProps {
  initialMood: string;
  onSettingsClick: () => void;
}
interface Message {
  type: 'user' | 'ai';
  content: string;
}
export function ChatModeScreen({
  initialMood,
  onSettingsClick
}: ChatModeScreenProps) {
  const [messages, setMessages] = useState<Message[]>([{
    type: 'user',
    content: initialMood
  }, {
    type: 'ai',
    content: "I've shared some Scripture, reflection, and a prayer for you above. How are you feeling now?"
  }]);
  const [input, setInput] = useState('');
  const quickActions = [{
    icon: BookOpen,
    label: 'Another verse',
    color: 'from-amber-400 to-orange-400'
  }, {
    icon: Sparkles,
    label: 'Deeper reflection',
    color: 'from-purple-400 to-pink-400'
  }, {
    icon: Music,
    label: 'Different song',
    color: 'from-green-400 to-emerald-400'
  }];
  const handleSend = () => {
    if (input.trim()) {
      setMessages([...messages, {
        type: 'user',
        content: input
      }]);
      setInput('');
      // Simulate AI response
      setTimeout(() => {
        setMessages(prev => [...prev, {
          type: 'ai',
          content: 'Let me find more encouragement for you...'
        }]);
      }, 1000);
    }
  };
  const handleQuickAction = (label: string) => {
    setMessages([...messages, {
      type: 'user',
      content: label
    }]);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        type: 'ai',
        content: `Here's ${label.toLowerCase()} for you...`
      }]);
    }, 1000);
  };
  return <div className="min-h-screen bg-gradient-to-b from-white to-sky-50 flex flex-col" data-id="element-61">
      <Header onSettingsClick={onSettingsClick} data-id="element-62" />

      {/* Chat History */}
      <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-63">
        <div className="max-w-md mx-auto space-y-4 pb-32" data-id="element-64">
          {messages.map((message, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-65">
              <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${message.type === 'user' ? 'bg-sky-400 text-white' : 'bg-white text-slate-700 border border-slate-200'}`} data-id="element-66">
                {message.content}
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white border-t border-slate-200 px-6 py-4" data-id="element-67">
        <div className="max-w-md mx-auto" data-id="element-68">
          <p className="text-xs text-slate-600 mb-3" data-id="element-69">Quick actions:</p>
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2" data-id="element-70">
            {quickActions.map(action => <button key={action.label} onClick={() => handleQuickAction(action.label)} className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-full hover:border-sky-300 hover:bg-sky-50 transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-sky-400" data-id="element-71">
                <div className={`w-5 h-5 bg-gradient-to-br ${action.color} rounded-full flex items-center justify-center`} data-id="element-72">
                  <action.icon className="w-3 h-3 text-white" data-id="element-73" />
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-74">
                  {action.label}
                </span>
              </button>)}
          </div>

          {/* Input */}
          <div className="flex gap-2" data-id="element-75">
            <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSend()} placeholder="Continue the conversation..." className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-full focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/20 text-slate-800 placeholder:text-slate-400" data-id="element-76" />
            <button onClick={handleSend} disabled={!input.trim()} className="p-3 bg-gradient-to-r from-sky-400 to-purple-400 text-white rounded-full hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Send message" data-id="element-77">
              <Send className="w-5 h-5" data-id="element-78" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}